import {EventEmitter, Inject, Injectable, LOCALE_ID} from '@angular/core';
import * as L from 'leaflet';
import {geoJSON, GeoJSONOptions, Icon, LatLng, LatLngBounds, LatLngBoundsExpression, Layer, marker} from 'leaflet';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {debounceTime, map} from 'rxjs/operators';
import './../../lib/leaflet-corridor';
import {AuthorizationService} from '../authorization/authorization.service';
import {GeopointFactoryService} from './geopoint-factory.service';
import {mapPane} from './models/map-pane.models';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  public static readonly leafletRegionsOptions: GeoJSONOptions = {
    pane: mapPane.REGION,
    style: {
      opacity: 0.5,
      fillOpacity: 0.1,
      dashArray: [4]
    }
  };
  markerLayers = new BehaviorSubject([]);
  geopointLayers = new BehaviorSubject([]);
  // geopointLayersGroup = new BehaviorSubject<LayerGroup>(null);
  geopointEditable = new BehaviorSubject([]);

  geofencesLayers = new BehaviorSubject<any[]>([]);
  // mask when draw in geofences and geopoints
  maskLayers = new BehaviorSubject<any[]>([]);

  linesLayers = new BehaviorSubject<L.GeoJSON<any>[]>([]);

  enableDraw = new BehaviorSubject(false);

  leafletGeofenceDraw = new BehaviorSubject<Layer>(null);

  leafletRegionDrawPathOptions = new BehaviorSubject({});

  leafletDisplayLayers = combineLatest(
    this.markerLayers,
    this.linesLayers,
    this.geofencesLayers.pipe(
      map(a => a.sort((i, j) => j.area - i.area).map(i => i.layer))
    ),
    this.maskLayers,
    this.geopointLayers.pipe(map(a => a.map(i => i.layer))),
    this.geopointEditable,
    this.leafletGeofenceDraw.pipe(
      map(
        layer => {
          if (layer) {
            layer.off('pm:edit', this.geofencePMEditEvent, this);
            layer.on('pm:edit', this.geofencePMEditEvent, this);
          }
          return layer;
        }
      )
    ),
    this.enableDraw
  ).pipe(
    debounceTime(100),
    map(
      ([markers, geolines, geozones, maskLayers, geopoints, geopointsEditable, drawLayer, enableDraw]) => {
        return [
          ...maskLayers,
          ...markers,
          ...geolines,
          ...geopoints,
          ...geozones,
          drawLayer,
          ...geopointsEditable
        ].filter(l => l !== null);
      }
    )
  );

  centerMapEvent = new EventEmitter();
  boundsEvent = new EventEmitter();

  mapReady = new BehaviorSubject<L.Map>(null);

  changeDetector = new EventEmitter();

  simpleIcon: Icon = new Icon({
    iconUrl: 'assets/leaflet-icons/marker-icon.png',
    shadowUrl: 'assets/leaflet-icons/marker-shadow.png',
    iconAnchor: [13, 40]
  });


  constructor(
    @Inject(LOCALE_ID) private locale: string,
    protected authorizationService: AuthorizationService,
    protected geopointFactoryService: GeopointFactoryService,
  ) {
  }

  geofencePMEditEvent(event) {
    // @ts-ignore
    this.leafletGeofenceDraw.next(event.target);
    this.changeDetector.emit();
  }
  public getInitialGeoBounds(): LatLngBoundsExpression {
    return new LatLngBounds(
      new LatLng(
        this.authorizationService.getIdentity().geoBounds.min_lat,
        this.authorizationService.getIdentity().geoBounds.min_lon
      ),
      new LatLng(
        this.authorizationService.getIdentity().geoBounds.max_lat,
        this.authorizationService.getIdentity().geoBounds.max_lon
      ),
    );
  }

  centerMap(lat, lon, zoom = 14) {
    this.centerMapEvent.emit({lat: lat, lon: lon, zoom: zoom});
  }

  bounds(layer) {
    this.boundsEvent.emit(layer);
  }

  setGeofences(layers: Array<any>) {
    this.geofencesLayers.next(layers);
  }

  setDrawPathOptions(options) {
    this.leafletRegionDrawPathOptions.next(options);
  }

  setDrawRadius(radius) {
    // @ts-ignore
    if (this.leafletGeofenceDraw.getValue().setRadius) {
      // @ts-ignore
      this.leafletGeofenceDraw.next(this.leafletGeofenceDraw.getValue().setRadius(radius));
    }
  }

  setMovableMarker(layer) {
    this.geopointEditable.next([layer]);
  }

  resizeMapWhenReady() {
    this.mapReady.subscribe(lMap => {
      if (!lMap) {
        return;
      }

      lMap.whenReady(() => {
        setTimeout(() => lMap.invalidateSize(), 0);
      });
    });
  }

  setGeopoints(geopoints) {
    const gp = geopoints.map(
      g => this.geopointFactoryService.createGeopointLayer(g)
    );
    this.geopointLayers.next(gp);
  }

  fitBounds(layer: L.Layer) {
    if ('getBounds' in layer) {
      this.mapReady.getValue().fitBounds((layer as any).getBounds());
    }
  }

  deleteAllMarkers() {
    this.markerLayers.next([]);
  }

  deleteAllLines() {
    this.linesLayers.next([]);
  }

  deleteAllLayers() {
    this.markerLayers.next([]);
    this.linesLayers.next([]);
    this.geofencesLayers.next([]);
    this.geopointLayers.next([]);
    this.geopointEditable.next([]);
    this.leafletGeofenceDraw.next(null);
  }
}
